import { useLocation } from "wouter";
import React, { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getJson } from "@/lib/http";
import { apiRequest } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable } from "@/components/tables/data-table";
import { createUserColumns } from "@/components/tables/user-columns";
import type { User as TableUser } from "@/components/tables/user-columns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ValidatedInput } from "@/components/ui/validated-input";
import {
  UserPlus,
  Edit,
  Trash2,
  Users,
  Shield,
  MapPin,
  Clock,
  CheckCircle,
  Copy,
  Mail,
  AlertTriangle,
  Package,
  Smartphone
} from "lucide-react";
import ResendInviteModal from "@/components/admin/ResendInviteModal";
import RevokeSessionsModal from "@/components/admin/RevokeSessionsModal";
import AppVersionsPanel from "@/components/admin/AppVersionsPanel";
import { TableSkeleton } from "@/components/skeletons/table-skeleton";

interface ApiUser {
  id: number;
  name: string;
  username: string;
  email: string | null;
  role: string;
  roles: string[];
  isActive: boolean;
  lastActiveAt?: string | null;
}

/** Edit-dialog payload from GET /api/users/:id (directory + kitting). */
interface ApiUserDetail extends ApiUser {
  contactName: string | null;
  contactPhone: string | null;
  deliveryAddressLine1: string | null;
  deliveryAddressLine2: string | null;
  deliveryTown: string | null;
  deliveryCounty: string | null;
  deliveryEircode: string | null;
}

interface CreateUserResponse {
  message?: string;
  warning?: string;
}

export default function UserManagement() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isAppVersionsOpen, setIsAppVersionsOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ApiUserDetail | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);
  const editLoadingIdRef = React.useRef<number | null>(null);
  const [passwordResetUser, setPasswordResetUser] = useState<any>(null);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [revokeSessionsUser, setRevokeSessionsUser] = useState<TableUser | null>(null);

  // Validation state for create form
  const [validationState, setValidationState] = useState({
    username: { isValid: true, message: '' },
    email: { isValid: true, message: '' }
  });

  const isFormValid = validationState.username.isValid && validationState.email.isValid;



  // Fetch users
  const { data: users = [], isLoading: usersLoading } = useQuery<ApiUser[]>({
    queryKey: ['/api/users'],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/users');
      const data = await getJson<ApiUser[]>('/api/users');
      console.log('[QUERY_DEBUG] /api/users response:', data?.length || 0, 'users');
      return Array.isArray(data) ? data : [];
    }
  });

  // Debug: Log users data when it changes
  React.useEffect(() => {
    if (users) {
      console.log('Users data updated:', users);
    }
  }, [users]);
  
  // Create user mutation
  const createUserMutation = useMutation<CreateUserResponse, Error, any>({
    mutationFn: async (userData: any) => {
      console.log('Mutation function called with:', userData);
      const response = await apiRequest('/api/users', 'POST', userData);
      return (await response.json()) as CreateUserResponse;
    },
    onSuccess: (data) => {
      console.log('User created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/users/operators'] });
      setIsCreateOpen(false);

      // Reset validation state
      setValidationState({
        username: { isValid: true, message: '' },
        email: { isValid: true, message: '' }
      });

      // Show appropriate message based on response
      if (data.warning) {
        toast({
          description: `${data.message || 'User invitation sent'} (${data.warning})`,
          variant: "default"
        });
      } else {
        toast({ description: data.message || "User has been invited by email" });
      }
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      console.error('User creation failed:', error);
      toast({
        description: error.message || "Failed to create user. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, ...userData }: any) =>
      apiRequest(`/api/users/${id}`, 'PUT', userData),
    onSuccess: () => {
      // Force complete cache invalidation and refetch
      queryClient.removeQueries({ queryKey: ['/api/users'] });
      queryClient.removeQueries({ queryKey: ['/api/users/operators'] });
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/users/operators'] });
      setEditingUser(null);
      toast({ description: "User updated successfully" });
    },
    onError: (error: Error) => {
      toast({ description: error.message, variant: "destructive" });
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest(`/api/users/${id}`, 'DELETE');
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const message = payload?.error || payload?.message || `Failed to delete user (HTTP ${response.status})`;
        const error: any = new Error(message);
        error.canDeactivate = payload?.canDeactivate || false;
        error.references = payload?.references;
        throw error;
      }
      return payload;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/users'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/users/operators'] })
      ]);
      setUserToDelete(null);
      toast({ description: "User deleted successfully" });
    },
    onError: (error: any) => {
      // Keep the dialog open if we can deactivate instead
      if (!error.canDeactivate) {
        setUserToDelete(null);
      }
      toast({
        description: error.message || "Failed to delete user",
        variant: "destructive",
        duration: 6000
      });
    }
  });

  // Deactivate user mutation (alternative to delete)
  const deactivateUserMutation = useMutation({
    mutationFn: ({ id }: any) =>
      apiRequest(`/api/users/${id}`, 'PUT', { isActive: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/users/operators'] });
      setUserToDelete(null);
      toast({ description: "User deactivated successfully" });
    },
    onError: (error: Error) => {
      toast({ description: error.message || "Failed to deactivate user", variant: "destructive" });
    }
  });

  const repairLoginMutation = useMutation({
    mutationFn: async (user: ApiUser) => {
      const response = await apiRequest(`/api/users/${user.id}/repair-login`, 'POST');
      return response.json();
    },
    onSuccess: async (result) => {
      toast({ description: result.message });
      await queryClient.invalidateQueries({ queryKey: ['/api/users'] });
    },
    onError: (error: Error) => toast({ description: error.message, variant: 'destructive' }),
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (user: any) => {
      const response = await apiRequest(`/api/users/${user.id}/resend-invite`, 'POST');
      const result = await response.json();
      if (!response.ok) {
        const message = result?.message || result?.error || 'Failed to resend invite';
        throw new Error(message);
      }
      return result;
    },
    onSuccess: async (result) => {
      toast({ description: result.message || 'Account setup email sent' });
      await queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      setPasswordResetUser(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Cannot Resend Invite',
        description: error.message || 'Failed to resend invite',
        variant: 'destructive',
        duration: 5000
      });
    }
  });

  const revokeSessionsMutation = useMutation({
    mutationFn: async (user: TableUser) => {
      const response = await apiRequest(`/api/users/${user.id}/revoke-sessions`, 'POST');
      const result = await response.json();
      if (!response.ok) {
        const message = result?.message || result?.error || 'Failed to sign out devices';
        throw new Error(message);
      }
      return result;
    },
    onSuccess: (data) => {
      toast({ description: data?.message || 'User signed out of all devices' });
      setRevokeSessionsUser(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Cannot Sign Out Devices',
        description: error.message || 'Failed to revoke user sessions',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });


  const handleCreateUser = (formData: FormData) => {
    const userData = {
      name: formData.get('name'),
      username: formData.get('username'),
      email: formData.get('email'),
      role: formData.get('role'),
    };
    console.log('Creating user with data:', userData);
    console.log('FormData entries:', Array.from(formData.entries()));
    createUserMutation.mutate(userData);
  };

  const handleUpdateUser = (formData: FormData) => {
    if (!editingUser) return;

    const userData = {
      id: editingUser.id,
      name: formData.get('name'),
      email: formData.get('email'),
      role: formData.get('role'),
      isActive: formData.get('isActive') === 'true',
      // Kitting/delivery fields
      contactName: formData.get('contactName') || null,
      contactPhone: formData.get('contactPhone') || null,
      deliveryAddressLine1: formData.get('deliveryAddressLine1') || null,
      deliveryAddressLine2: formData.get('deliveryAddressLine2') || null,
      deliveryTown: formData.get('deliveryTown') || null,
      deliveryCounty: formData.get('deliveryCounty') || null,
      deliveryEircode: formData.get('deliveryEircode') || null,
    };
    updateUserMutation.mutate(userData);
  };




  const getUserStatusBadge = (user: any) => {
    if (user.isActive) {
      return <Badge variant="success">Active</Badge>;
    }
    return <Badge variant="destructive">Archived</Badge>;
  };

  const openEditUser = async (user: TableUser) => {
    if (editLoadingIdRef.current !== null) return;
    editLoadingIdRef.current = user.id;
    setEditLoadingId(user.id);
    try {
      const detail = await getJson<ApiUserDetail>(`/api/users/${user.id}`);
      setEditingUser(detail);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load user details';
      toast({
        title: 'Cannot open user',
        description: message,
        variant: 'destructive',
      });
    } finally {
      editLoadingIdRef.current = null;
      setEditLoadingId(null);
    }
  };

  // Create columns for DataTable
  const columns = useMemo(
    () => createUserColumns({
      onEdit: (user) => {
        void openEditUser(user);
      },
      onDelete: (user) => {
        console.log('Delete button clicked for user:', user);
        setUserToDelete(user);
      },
      onResendInvite: setPasswordResetUser,
      onDeviceHealth: (user) => setLocation(`/admin/activation-cockpit/by-operator?operatorId=${user.id}`),
      onRevokeSessions: setRevokeSessionsUser,
    }),
    // toast is stable enough for this page; openEditUser closes over current toast
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const usersTableData: TableUser[] = useMemo(
    () => users.map((user) => ({
      ...user,
      role: user.role === "admin" ? "admin" : "operator",
    })),
    [users],
  );
  console.warn('[UserManagement] render snapshot', {
    rowCount: usersTableData.length,
    roles: usersTableData.map((u) => Array.isArray(u?.roles) ? u.roles : []),
    sampleUser: usersTableData[0] || null
  });

  if (usersLoading) {
    console.warn('[UserManagement] still loading users', { hasUsersValue: !!users });
    return (
      <div className="container mx-auto p-6 space-y-6">
        <PageHeader
          icon={Users}
          title="User Management"
          description="Invite and manage users and operators"
        >
          <Button onClick={() => setIsCreateOpen(true)} className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Invite User
          </Button>
        </PageHeader>
        <TableSkeleton rows={8} />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader icon={Users} title="User Management">
        <Dialog open={isAppVersionsOpen} onOpenChange={setIsAppVersionsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="mr-2">
              <Smartphone className="h-4 w-4 mr-2" />
              App versions
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Operator app versions</DialogTitle>
            </DialogHeader>
            <AppVersionsPanel />
          </DialogContent>
        </Dialog>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!isFormValid) {
                toast({
                  description: "Please fix validation errors before submitting",
                  variant: "destructive"
                });
                return;
              }
              const formData = new FormData(e.currentTarget);
              handleCreateUser(formData);
            }} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" name="name" required />
              </div>
              <ValidatedInput
                id="username"
                name="username"
                label="Username"
                required
                validationEndpoint="/api/users/validate/username"
                onValidationChange={(isValid, message) =>
                  setValidationState(prev => ({
                    ...prev,
                    username: { isValid, message: message || '' }
                  }))
                }
              />
              <ValidatedInput
                id="email"
                name="email"
                label="Email"
                type="email"
                required
                validationEndpoint="/api/users/validate/email"
                onValidationChange={(isValid, message) =>
                  setValidationState(prev => ({
                    ...prev,
                    email: { isValid, message: message || '' }
                  }))
                }
              />
              <div>
                <Label htmlFor="role">Role</Label>
                <Select name="role" required defaultValue="operator">
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="operator">Operator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createUserMutation.isPending || !isFormValid}
                  className={!isFormValid ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {createUserMutation.isPending ? "Sending Invite..." : "Send Invitation"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">All Users</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                All Users ({users?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={usersTableData}
                searchKey="name"
                searchPlaceholder="Search by name or email..."
                showColumnToggle={true}
                isLoading={usersLoading}
              />
            </CardContent>
          </Card>
        </TabsContent>



      </Tabs>

      {/* Edit User Dialog */}
      {
        editingUser && (
          <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
              </DialogHeader>
              <form
                key={editingUser.id}
                onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleUpdateUser(formData);
              }} className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Name</Label>
                  <Input id="edit-name" name="name" defaultValue={editingUser.name || ''} required />
                </div>
                <div>
                  <Label htmlFor="edit-email">Email</Label>
                  <Input id="edit-email" name="email" type="email" defaultValue={editingUser.email || ''} />
                </div>
                <div>
                  <Label htmlFor="edit-role">Role</Label>
                  <Select name="role" defaultValue={editingUser.role} required>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Current active roles: {Array.isArray(editingUser.roles) ? editingUser.roles.join(', ') : 'None'}
                  </p>
                </div>
                <div>
                  <Label htmlFor="edit-isActive">Account Status</Label>
                  <Select name="isActive" defaultValue={editingUser.isActive ? 'true' : 'false'} required>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Archived users cannot log in to the system
                  </p>
                </div>

                {/* Kitting/Delivery Section - Only for operators */}
                {editingUser.role === 'operator' && (
                  <>
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Kitting & Delivery Information
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-contactName">Contact Name</Label>
                        <Input 
                          id="edit-contactName" 
                          name="contactName" 
                          defaultValue={editingUser.contactName || ''} 
                          placeholder="Primary contact"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-contactPhone">Contact Phone</Label>
                        <Input 
                          id="edit-contactPhone" 
                          name="contactPhone" 
                          defaultValue={editingUser.contactPhone || ''} 
                          placeholder="+353..."
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="edit-deliveryAddressLine1">Delivery Address Line 1</Label>
                      <Input 
                        id="edit-deliveryAddressLine1" 
                        name="deliveryAddressLine1" 
                        defaultValue={editingUser.deliveryAddressLine1 || ''} 
                        placeholder="Street address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-deliveryAddressLine2">Delivery Address Line 2</Label>
                      <Input 
                        id="edit-deliveryAddressLine2" 
                        name="deliveryAddressLine2" 
                        defaultValue={editingUser.deliveryAddressLine2 || ''} 
                        placeholder="Apartment, unit, etc. (optional)"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="edit-deliveryTown">Town</Label>
                        <Input 
                          id="edit-deliveryTown" 
                          name="deliveryTown" 
                          defaultValue={editingUser.deliveryTown || ''} 
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-deliveryCounty">County</Label>
                        <Input 
                          id="edit-deliveryCounty" 
                          name="deliveryCounty" 
                          defaultValue={editingUser.deliveryCounty || ''} 
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-deliveryEircode">Eircode</Label>
                        <Input 
                          id="edit-deliveryEircode" 
                          name="deliveryEircode" 
                          defaultValue={editingUser.deliveryEircode || ''} 
                          placeholder="e.g. D02 X285"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateUserMutation.isPending}>
                    {updateUserMutation.isPending ? "Updating..." : "Update User"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )
      }

      {/* Password Reset Modal */}
      <ResendInviteModal
        key={passwordResetUser?.id}
        user={passwordResetUser}
        isOpen={!!passwordResetUser}
        onClose={() => setPasswordResetUser(null)}
        onResend={(user) => resendInviteMutation.mutate(user)}
        onRepair={(user) => repairLoginMutation.mutate(user)}
        isSubmitting={resendInviteMutation.isPending || repairLoginMutation.isPending}
      />



      <RevokeSessionsModal
        user={revokeSessionsUser}
        isOpen={!!revokeSessionsUser}
        isSubmitting={revokeSessionsMutation.isPending}
        onClose={() => setRevokeSessionsUser(null)}
        onConfirm={() => {
          if (revokeSessionsUser) {
            revokeSessionsMutation.mutate(revokeSessionsUser);
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      {console.log('userToDelete state:', userToDelete)}
      {
        userToDelete && (
          <Dialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Delete User
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p>
                  Are you sure you want to delete <strong>{userToDelete.name}</strong> ({userToDelete.email})?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> This action cannot be undone. The user will be removed from both
                    the local database and Supabase authentication system.
                  </p>
                </div>
                {deleteUserMutation.error && (deleteUserMutation.error as any).canDeactivate && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <p className="text-sm text-amber-900 mb-2">
                      <strong>Cannot delete this user:</strong> They have historical records that must be preserved.
                    </p>
                    <p className="text-sm text-amber-800">
                      Instead, you can <strong>deactivate</strong> this user to prevent them from logging in
                      while keeping all historical data intact.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setUserToDelete(null)}
                  disabled={deleteUserMutation.isPending || deactivateUserMutation.isPending}
                >
                  Cancel
                </Button>
                {deleteUserMutation.error && (deleteUserMutation.error as any).canDeactivate ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => deactivateUserMutation.mutate({ id: userToDelete.id })}
                    disabled={deactivateUserMutation.isPending}
                  >
                    {deactivateUserMutation.isPending ? "Deactivating..." : "Deactivate User"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => deleteUserMutation.mutate(userToDelete.id)}
                    disabled={deleteUserMutation.isPending}
                  >
                    {deleteUserMutation.isPending ? "Deleting..." : "Delete User"}
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )
      }

    </div >
  );
}
